import { func, bool, string, node, array, oneOfType, element, arrayOf, oneOf, shape } from 'prop-types';
import { Button as BootstrapButton } from 'react-bootstrap';
import './styles.scss';

const renderCustomButton = (buttonConfig, index) => {
  const {
    key,
    label,
    icon,
    onClick,
    variant = 'primary',
    disabled = false,
    className = '',
    title = '',
    ...restProps
  } = buttonConfig;

  return (
    <BootstrapButton
      key={key || label || `custom-btn-${crypto.randomUUID()}`}
      type="button"
      variant={variant}
      onClick={onClick}
      disabled={disabled}
      className={className}
      title={title}
      {...restProps}
    >
      {icon && <i className={icon}></i>}
      {icon && label && <span className="ms-2">{label}</span>}
      {!icon && label}
    </BootstrapButton>
  );
};

const GridActions = ({
  onSearch = null,
  onClearFilters,
  onExport = null,
  onView = null,
  onManageColumns = null,
  onToggleFilters,
  showExport = true,
  showResults = false,
  totalItems = 0,
  loading = false,
  exportButtonText = 'Exportar',
  viewButtonText = 'Vista',
  manageColumnsButtonText = 'Administrar columnas',
  disabled = false,
  className = '',
  children = null,
  customButtons = [],
  customButtonsPosition = 'start',
  hasFilters = true,
  showSearchInput = false,
  searchValue = '',
  onSearchChange = null,
  searchPlaceholder = 'Buscar...',
  searchInputClassName = '',
  onKeyDown = null,
}) => {
  const buttonsAtStart = customButtonsPosition === 'start';
  const customButtonsStart = buttonsAtStart ? customButtons : [];
  const customButtonsEnd = buttonsAtStart ? [] : customButtons;

  return (
    <div className={`grid-actions ${className}`}>
      <div className="grid-actions__header justify-content-end mt-2">
        {showSearchInput && (
          <div className="grid-actions__search">
            <div className="input-group">
              <input
                type="text"
                className={`form-control ${searchInputClassName}`}
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={onSearchChange}
                onKeyDown={onKeyDown}
                disabled={disabled}
              />
              <BootstrapButton
                variant="primary"
                type="button"
                onClick={onSearch}
                disabled={disabled}
              >
                <i className="bi bi-search"></i>
              </BootstrapButton>
            </div>
          </div>
        )}

        {hasFilters && (
          <div className="grid-actions__filters">
            <BootstrapButton
              type="button"
              variant="primary"
              onClick={onToggleFilters}
              disabled={disabled}
            >
              <i className="bi bi-funnel"></i>
            </BootstrapButton>
            <BootstrapButton
              type="button"
              variant="outline-secondary"
              onClick={onClearFilters}
              disabled={disabled}
            >
              Limpiar
            </BootstrapButton>
          </div>
        )}
      </div>

      <div className="grid-actions__bar">
        <div className="grid-actions__info">
          {!loading && (
            <span className="grid-actions__count">{`${totalItems} registros`}</span>
          )}
        </div>

        <div className="grid-actions__buttons">
          {customButtonsStart.map((btn, index) =>
            btn.element || renderCustomButton(btn, index)
          )}

          {showExport && showResults && (
            <BootstrapButton
              type="button"
              variant="primary"
              onClick={onExport}
              disabled={disabled}
            >
              <i className="bi bi-download"> </i>
              {exportButtonText}
            </BootstrapButton>
          )}

          {onView && (
            <BootstrapButton
              type="button"
              variant="outline-primary"
              onClick={onView}
              disabled={disabled}
            >
              <i className="bi bi-table"></i>
              <span className="ms-2">{viewButtonText}</span>
            </BootstrapButton>
          )}

          {onManageColumns && (
            <BootstrapButton
              type="button"
              variant="primary"
              onClick={onManageColumns}
              disabled={disabled}
            >
              <i className="bi bi-gear"></i>
              <span className="ms-2">{manageColumnsButtonText}</span>
            </BootstrapButton>
          )}

          {/* Botones personalizados al final */}
          {customButtonsEnd.map((btn, index) =>
            btn.element || renderCustomButton(btn, index)
          )}

          {/* Children para máxima flexibilidad */}
          {children}
        </div>
      </div>
    </div>
  );
};

GridActions.propTypes = {
  onSearch: func,
  onClearFilters: func.isRequired,
  onExport: func,
  onView: func,
  onManageColumns: func,
  onToggleFilters: func.isRequired,
  showExport: bool,
  showResults: bool,
  totalItems: node,
  loading: bool,
  searchButtonText: string,
  clearFiltersButtonText: string,
  exportButtonText: string,
  viewButtonText: string,
  manageColumnsButtonText: string,
  disabled: bool,
  className: string,
  children: node,
  // Nueva prop para botones personalizados
  customButtons: arrayOf(
    oneOfType([
      // Configuración de botón
      shape({
        label: string,
        icon: string,
        onClick: func.isRequired,
        variant: string,
        disabled: bool,
        className: string,
        title: string,
        key: string,
      }),
      // Elemento React directo
      element
    ])
  ),
  customButtonsPosition: oneOf(['start', 'end']),
  showSearchInput: bool,
  searchValue: string,
  onSearchChange: func,
  searchPlaceholder: string,
  searchInputClassName: string,
  onKeyDown: func,
  title: string
};

export default GridActions;
